import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { EditorView } from '@codemirror/view';
import {
  insertCodeBlock,
  insertHorizontalRule,
  insertLink,
  setHeading,
  toggleBlockquote,
  toggleBold,
  toggleBulletList,
  toggleInlineCode,
  toggleItalic,
  toggleOrderedList,
} from './commands.ts';

interface ToolbarProps {
  viewRef: MutableRefObject<EditorView | null>;
  position: 'top' | 'bottom';
}

/**
 * Formatting toolbar. Sticky at the top on desktop, bottom on
 * coarse-pointer (mobile). The H button opens a small popover with
 * H1/H2/H3/Body. Other buttons drive commands directly.
 */
export function MarkdownToolbar({ viewRef, position }: ToolbarProps) {
  const [headingOpen, setHeadingOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Click-away to close the heading popover.
  useEffect(() => {
    if (!headingOpen) return;
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setHeadingOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [headingOpen]);

  const run = (fn: (v: EditorView) => boolean) => () => {
    const v = viewRef.current;
    if (!v) return;
    fn(v);
    v.focus();
  };

  return (
    <div
      className={`md-toolbar md-toolbar-${position}`}
      role="toolbar"
      aria-label="Formatting"
      data-position={position}
    >
      <div className="md-toolbar-heading-wrap" ref={popoverRef}>
        <button
          type="button"
          className="md-toolbar-btn"
          aria-haspopup="menu"
          aria-expanded={headingOpen}
          aria-label="Heading"
          onClick={() => setHeadingOpen((o) => !o)}
        >
          H<span className="md-toolbar-chev" aria-hidden>▾</span>
        </button>
        {headingOpen ? (
          <div className="md-toolbar-popover" role="menu">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                run((v) => setHeading(v, 1))();
                setHeadingOpen(false);
              }}
            >
              Heading 1
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                run((v) => setHeading(v, 2))();
                setHeadingOpen(false);
              }}
            >
              Heading 2
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                run((v) => setHeading(v, 3))();
                setHeadingOpen(false);
              }}
            >
              Heading 3
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                run((v) => setHeading(v, 0))();
                setHeadingOpen(false);
              }}
            >
              Body
            </button>
          </div>
        ) : null}
      </div>

      <button type="button" className="md-toolbar-btn" aria-label="Bold" onClick={run(toggleBold)}>
        <b>B</b>
      </button>
      <button type="button" className="md-toolbar-btn" aria-label="Italic" onClick={run(toggleItalic)}>
        <i>I</i>
      </button>
      <button type="button" className="md-toolbar-btn" aria-label="Inline code" onClick={run(toggleInlineCode)}>
        {'</>'}
      </button>
      <button type="button" className="md-toolbar-btn" aria-label="Bullet list" onClick={run(toggleBulletList)}>
        ●
      </button>
      <button type="button" className="md-toolbar-btn" aria-label="Numbered list" onClick={run(toggleOrderedList)}>
        1.
      </button>
      <button type="button" className="md-toolbar-btn" aria-label="Quote" onClick={run(toggleBlockquote)}>
        ❝
      </button>
      <button type="button" className="md-toolbar-btn" aria-label="Link" onClick={run((v) => insertLink(v))}>
        🔗
      </button>
      <button type="button" className="md-toolbar-btn" aria-label="Code block" onClick={run(insertCodeBlock)}>
        {'{ }'}
      </button>
      <button
        type="button"
        className="md-toolbar-btn"
        aria-label="Horizontal rule"
        onClick={run(insertHorizontalRule)}
      >
        ─
      </button>
    </div>
  );
}
